import type { PickStopDto } from '@eombor/contracts';

/**
 * YIG'IB OLISH SEANSI — skanerlangan kod nimani anglatadi.
 *
 * Fayl SOF: DOM ni ham, so'rovni ham bilmaydi. Sabab tajribadan — bu
 * yerdagi chegaraviy hollarni brauzerda tekshirib bo'lmaydi: bir xil
 * kodni ikki marta skanerlash, mahsulotni katakdan oldin skanerlash,
 * bitta katakda ikkita partiya yotishi. Ularning har biri uchun ombor
 * qurib bo'lmaydi, sof funksiya esa hammasini bir zumda sinaydi.
 */

/**
 * To'xtashning holati — IKKITA.
 *
 * Ilgari uchta edi va o'rtadagisi «javon yorlig'i o'qildi, endi quti
 * kutilyapti» degani edi: skanerlash ikki qadamli bo'lib, avval javon,
 * keyin quti o'qitilardi.
 *
 * Ikki qadam OLIB TASHLANDI. Sabab oddiy: qutidagi etiketka partiyaniki
 * va reja o'sha partiya QAYSI KATAKDA turganini allaqachon biladi —
 * ya'ni mahsulotni skanerlashning o'zi «qaysi javondan olindi» degan
 * savolga ham javob beradi. Javon yorlig'ini alohida o'qitish o'sha
 * javobni ikkinchi marta so'rash edi va har o'rinda bitta ortiqcha
 * harakat qo'shardi.
 */
export type StopStatus = 'pending' | 'taken';

export interface StopState {
  status: StopStatus;
  /** Haqiqatda olingan miqdor. `taken` bo'lmaguncha `null`. */
  issuedQty: number | null;
  /** Rejadagidan kam olingan bo'lsa — sababi. */
  note: string | null;
}

export const initialStates = (stops: readonly PickStopDto[]): StopState[] =>
  stops.map(() => ({ status: 'pending', issuedQty: null, note: null }));

/**
 * Skanerlangan matnni solishtirishga tayyorlaydi.
 *
 * Katta harf va chetdagi bo'shliq olib tashlanadi: skaner ba'zan
 * `Shift` bilan yuboradi va qo'lda kiritilgan kodda ortiqcha probel
 * qoladi. Ichki chiziqchalarga TEGILMAYDI — ular kodning o'z qismi
 * (`A-03-02-05`, `P-2026-0412`).
 */
export const normalizeScan = (raw: string): string => raw.trim().toUpperCase();

/**
 * Skanerlash NATIJASI.
 *
 * `unknown` da `code` qoladi va ekran uni ko'rsatadi: «bu boshqa
 * mahsulot» degan xabar qaysi kod o'qilganini aytmasa, skladchi xatoni
 * topa olmaydi.
 */
export type ScanResult =
  /** Qutidagi etiketka o'qildi — o'rin yopiladi. */
  | { kind: 'productOk'; index: number }
  /**
   * Javon yorlig'i o'qildi — DIQQATNI o'sha o'ringa ko'chiradi.
   *
   * Majburiy emas: skladchi to'g'ri javonga borganini ekranda ko'rish
   * uchun o'qitishi mumkin, lekin o'rinni yopadigan narsa — qutidagi
   * etiketka.
   */
  | { kind: 'focus'; index: number }
  /** Shu kod allaqachon yopilgan to'xtashga tegishli. */
  | { kind: 'alreadyDone'; index: number }
  | { kind: 'unknown'; code: string };

/**
 * Kod qaysi to'xtashga tegishli.
 *
 * Tartib MUHIM: avval katak, keyin mahsulot. Ikkovi bir xil bo'lishi
 * mumkin emas — katak kodi harf bilan boshlanadi (`A-03-02-05`), partiya
 * raqami esa `P-YYYY-NNNN` shaklida va ikkinchi bo'lagi to'rt xonali yil
 * (`stock/domain/batch-number.ts`). Shunga qaramay tartib qat'iy: bir kun
 * formatlar yaqinlashib qolsa, skaner javonni mahsulot deb o'qimasin.
 *
 * Qidiruv YOPILMAGAN to'xtashlardan boshlanadi: bitta katakda bir necha
 * partiya yotishi oddiy hol va allaqachon olingan qator yangi skanerlashni
 * o'ziga tortib ketmasligi kerak.
 */
export const classifyScan = (
  stops: readonly PickStopDto[],
  states: readonly StopState[],
  raw: string,
): ScanResult => {
  const code = normalizeScan(raw);
  if (code === '') return { kind: 'unknown', code };

  const at = (index: number): StopState | undefined => states[index];

  // 1. QUTIDAGI ETIKETKA — o'rinni YOPADIGAN kod.
  const productMatches = stops.flatMap((stop, index) =>
    normalizeScan(stop.barcode) === code ? [index] : [],
  );
  if (productMatches.length > 0) {
    /* Bitta partiya bir nechta katakda yotishi mumkin va shunda bitta
       etiketka ikki o'ringa to'g'ri keladi. Birinchi YOPILMAGANI
       olinadi: skladchi javonlarni tartib bilan aylanadi. */
    const open = productMatches.find((index) => at(index)?.status === 'pending');
    if (open !== undefined) return { kind: 'productOk', index: open };
    return { kind: 'alreadyDone', index: productMatches[0] ?? 0 };
  }

  // 2. JAVON YORLIG'I — ixtiyoriy, faqat diqqatni ko'chiradi.
  const cellMatches = stops.flatMap((stop, index) =>
    normalizeScan(stop.cellCode) === code ? [index] : [],
  );
  if (cellMatches.length > 0) {
    const open = cellMatches.find((index) => at(index)?.status === 'pending');
    if (open !== undefined) return { kind: 'focus', index: open };
    return { kind: 'alreadyDone', index: cellMatches[0] ?? 0 };
  }

  return { kind: 'unknown', code };
};

/**
 * Bitta to'xtashni YOPADI.
 *
 * Miqdor ATAYLAB argument: odatda rejadagining o'zi, lekin javonda kam
 * chiqsa skladchi kamaytiradi va sabab yozadi. Sababsiz kamaytirish shu
 * yerda ham, serverda ham o'tmaydi — ikki qavat bitta qoida.
 */
export const takeStop = (
  states: readonly StopState[],
  index: number,
  issuedQty: number,
  note: string | null,
): StopState[] =>
  states.map((state, at) =>
    at === index ? { status: 'taken' as const, issuedQty, note } : state,
  );

/** Hamma to'xtash yopildimi — «Berish» tugmasi shundan ochiladi. */
export const allTaken = (states: readonly StopState[]): boolean =>
  states.length > 0 && states.every((state) => state.status === 'taken');

/**
 * To'xtashlarni TALABNOMA SATRIGA yig'adi — server aynan shu shaklni kutadi.
 *
 * Bitta satr bir nechta katakdan to'lishi mumkin va server satr bo'yicha
 * BITTA miqdor kutadi. Sabab ham bittaga birlashadi: ikki katakda ikki xil
 * sabab bo'lsa, ular nuqtali vergul bilan qo'shiladi — birinchisini tanlab,
 * ikkinchisini tashlab yuborish ma'lumot yo'qotish bo'lardi.
 */
export const handoverLinesOf = (
  stops: readonly PickStopDto[],
  states: readonly StopState[],
): { lineId: string; issuedQty: number; note?: string }[] => {
  const byLine = new Map<string, { issuedQty: number; notes: string[] }>();

  for (const [index, stop] of stops.entries()) {
    const state = states[index];
    if (state === undefined || state.status !== 'taken') continue;

    const row = byLine.get(stop.lineId) ?? { issuedQty: 0, notes: [] };
    row.issuedQty = Math.round((row.issuedQty + (state.issuedQty ?? 0)) * 1000) / 1000;
    if (state.note !== null && state.note !== '' && !row.notes.includes(state.note)) {
      row.notes.push(state.note);
    }
    byLine.set(stop.lineId, row);
  }

  return [...byLine.entries()].map(([lineId, row]) => ({
    lineId,
    issuedQty: row.issuedQty,
    ...(row.notes.length === 0 ? {} : { note: row.notes.join('; ').slice(0, 200) }),
  }));
};
